import { ArrowLeft, ChevronRight, MessageSquare, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import api from '../../api'
import Colors from '../../assets/styles/Colors'
import type { AIChats as AIChatsType } from '../../domain/types'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

interface AIChatsProps {
  onBack: () => void
  onChatSelect?: (conversationId: string) => void
  isVisible: boolean
  onChatDeleted?: (conversationId: string) => void
}

const ChatsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  max-width: 850px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  overflow-y: auto;
`

const ChatsHeader = styled.div`
  margin-bottom: 1.5rem;
`

const HeaderTop = styled.div`
  display: flex;
  align-items: start;
  gap: 1rem;
`

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: ${Colors.lightRed};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${Colors.primaryRed};

  &:hover {
    background: ${Colors.primaryRed};
    color: ${Colors.white};
  }
`

const ChatsTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${Colors.Black};
  margin: 0;
`

const ChatsSubtitle = styled.p`
  font-size: 14px;
  color: ${Colors.primaryGrey};
  margin-top: 0.5rem;
  margin-left: 3.3rem;
`

const ChatsContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const ChatsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`

const ChatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: ${Colors.white};
  border: 1px solid ${Colors.lightGrey};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${Colors.primaryRed};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`

const ChatIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${Colors.lightRed};
  border-radius: 8px;
  flex-shrink: 0;
`

const ChatInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const ChatTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${Colors.Black};
  word-break: break-word;
`

const ChatDate = styled.div`
  font-size: 12px;
  color: ${Colors.primaryGrey};
`

const ChatActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: ${Colors.lightRed};
  color: ${Colors.primaryRed};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${Colors.primaryRed};
    color: ${Colors.white};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: ${Colors.lightGrey};
    color: ${Colors.primaryGrey};
  }
`

const ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${Colors.lightRed};
  transition: all 0.2s ease;
  flex-shrink: 0;

  ${ChatItem}:hover & {
    background: ${Colors.primaryRed};
    color: ${Colors.white};
  }
`

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  color: ${Colors.primaryGrey};
  padding: 2rem;
`

const LoadingState = styled(EmptyState)`
  font-size: 16px;
`

export default function AIChats({ onBack, onChatSelect, isVisible, onChatDeleted }: AIChatsProps) {
  const { t } = useTranslation()
  const { data: chats, isLoading: isChatsLoading, refetch } = api.useGetAIChatsQuery(undefined)
  const [deleteChat, { isLoading: isDeleting }] = api.useDeleteAIChatMutation()
  const [activeDeleteId, setActiveDeleteId] = useState<string | null>(null)

  // Refetch chats when the chats view becomes visible (user clicks Chats menu item)
  useEffect(() => {
    if (isVisible) {
      refetch()
    }
  }, [isVisible, refetch])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <ChatsContainer>
      <ChatsHeader>
        <HeaderTop>
          <BackButton onClick={onBack}>
            <ArrowLeft size={20} />
          </BackButton>
          <ChatsTitle>{t('ai.chatHistory')}</ChatsTitle>
        </HeaderTop>
        <ChatsSubtitle>
          {chats && chats.length > 0 ? t('ai.conversations_other', { count: chats.length }) : t('ai.noConversations')}
        </ChatsSubtitle>
      </ChatsHeader>

      <ChatsContent>
        {isChatsLoading && <LoadingState>{t('ai.loadingChats')}</LoadingState>}

        {!isChatsLoading && (!chats || chats.length === 0) && (
          <EmptyState>
            <MessageSquare size={48} />
            <div>{t('ai.noConversations')}</div>
          </EmptyState>
        )}

        {!isChatsLoading && chats && chats.length > 0 && (
          <ChatsList>
            {chats.map((chat: AIChatsType) => (
              <ChatItem
                key={chat.conversationId}
                onClick={() => {
                  if (onChatSelect) {
                    onChatSelect(chat.conversationId)
                  }
                }}
              >
                <ChatIconWrapper>
                  <MessageSquare size={24} color={Colors.primaryRed} />
                </ChatIconWrapper>
                <ChatInfo>
                  <ChatTitle>{chat.title}</ChatTitle>
                  <ChatDate>{formatDate(chat.createdAt)}</ChatDate>
                </ChatInfo>
                <ChatActions>
                  <DeleteButton
                    aria-label={t('ai.deleteChat')}
                    onClick={async (event) => {
                      event.stopPropagation()
                      if (activeDeleteId) return
                      setActiveDeleteId(chat.conversationId)
                      try {
                        await deleteChat(chat.conversationId).unwrap()
                        toast.success(String(t('ai.chatDeleted')))
                        onChatDeleted?.(chat.conversationId)
                        refetch()
                      } catch (error) {
                        console.info('Failed to delete chat', error)
                        toast.error(String(t('ai.chatDeleteFailed')))
                      } finally {
                        setActiveDeleteId(null)
                      }
                    }}
                    disabled={isDeleting && activeDeleteId === chat.conversationId}
                    title={t('ai.deleteChat')}
                    type="button"
                  >
                    <Trash2 size={16} />
                  </DeleteButton>
                  <ChevronIcon>
                    <ChevronRight size={16} />
                  </ChevronIcon>
                </ChatActions>
              </ChatItem>
            ))}
          </ChatsList>
        )}
      </ChatsContent>
    </ChatsContainer>
  )
}
